<template>
	<div v-if="intestazioni.length > 0 && !aperto" class="small text-success mb-3">
		<i class="ph-check-circle me-1"></i>Colonne del file riconosciute<span v-if="nomePresetApplicato"> (preset "{{ nomePresetApplicato }}")</span>
		<a href="#" class="ms-2" @click.prevent="aperto = true">Mostra abbinamenti</a>
	</div>
	<div v-else-if="intestazioni.length > 0" class="border rounded p-2 mb-3">
		<div class="d-flex flex-wrap align-items-center gap-2 mb-2">
			<strong class="me-auto">Abbina le colonne del file</strong>
			<select v-model="idPreset" class="form-select form-select-sm" style="width:auto;" @change="applicaPresetSelezionato()">
				<option :value="null">-- Preset --</option>
				<option v-for="p in preset" :key="p.ID_IMPORT_MAPPING" :value="p.ID_IMPORT_MAPPING">{{ p.NOME }}</option>
			</select>
			<button v-if="idPreset && $root.hasFunzionalita('IMPORTMAPPING_DELETE')" type="button" class="btn btn-sm btn-outline-danger" title="Elimina preset" @click="eliminaPreset()">
				<i class="ph-trash"></i>
			</button>
		</div>

		<div v-if="messaggioMatch" class="small text-muted mb-2">{{ messaggioMatch }}</div>

		<table class="table table-sm mb-2">
			<thead>
				<tr>
					<th style="width:30%;">Campo</th>
					<th style="width:35%;">Colonna del file</th>
					<th>
						Esempio
						<span v-if="righe.length > 1" class="ms-1 text-nowrap">
							<a href="#" @click.prevent="rigaEsempio = (rigaEsempio + righe.length - 1) % righe.length">&lsaquo;</a>
							<small class="text-muted">{{ rigaEsempio + 1 }}/{{ righe.length }}</small>
							<a href="#" @click.prevent="rigaEsempio = (rigaEsempio + 1) % righe.length">&rsaquo;</a>
						</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="campo in campi" :key="campo.key">
					<td :class="{ 'text-danger': campo.required && !mapping[campo.key] }">
						{{ campo.label }}<span v-if="campo.required"> *</span>
					</td>
					<td>
						<select v-model="mapping[campo.key]" class="form-select form-select-sm">
							<option value="">-- nessuna --</option>
							<option v-for="h in intestazioni" :key="h" :value="h" :disabled="isUsata(h, campo.key)">{{ h }}</option>
						</select>
					</td>
					<td class="small text-muted text-truncate" style="max-width:200px;">{{ esempio(campo) }}</td>
				</tr>
			</tbody>
		</table>

		<div v-if="$root.hasFunzionalita('IMPORTMAPPING_CREATE')" class="input-group input-group-sm">
			<input v-model="nomePreset" type="text" class="form-control" placeholder="Nome preset (es. Banca X)" />
			<button type="button" class="btn btn-outline-secondary" :disabled="!nomePreset || !valido" @click="salvaPreset()">Salva come preset</button>
		</div>
	</div>
</template>

<script>
// Passo di mappatura generico per gli import Excel: legge intestazioni + prime righe (modalita core anteprimaExcel),
// propone gli abbinamenti (preset salvato > nome/alias > fuzzy) ed emette {CAMPO_CANONICO: "Intestazione file"}
// da passare come MAPPING agli args dell'upload (ExcelReader2::setMapping).
const normalizza = (s) => String(s == null ? '' : s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');

const levenshtein = (a, b) => {
	let prec = Array.from({ length: b.length + 1 }, (_, j) => j);
	for (let i = 1; i <= a.length; i++) {
		const riga = [i];
		for (let j = 1; j <= b.length; j++) {
			riga[j] = Math.min(prec[j] + 1, riga[j - 1] + 1, prec[j - 1] + (a[i - 1] == b[j - 1] ? 0 : 1));
		}
		prec = riga;
	}
	return prec[b.length];
};

const SOGLIA_FUZZY = 0.65;

export default {
	name: 'ExcelMapper',
	props: {
		file:       { type: Object, default: null },  // File scelto dall'utente
		tipoImport: { type: String, required: true }, // chiave dei preset, es. 'NOTE_SPESE'
		campi:      { type: Array, required: true },  // [{ key, label, required, alias: [], tipo: 'data' }]
		modelValue: { type: Object, default: () => ({}) },
	},
	emits: ['update:modelValue', 'valido'],
	data() {
		return {
			intestazioni:   [],
			righe:          [],
			rigaEsempio:    0,
			preset:         [],
			idPreset:       null,
			nomePreset:     '',
			mapping:        {},
			messaggioMatch: '',
			aperto:         true,  // false = file gia' a posto, tabella nascosta
		};
	},
	computed: {
		nomePresetApplicato() {
			const p = utils.array.select(this.preset, 'ID_IMPORT_MAPPING', this.idPreset, 1)[0];
			return p ? p.NOME : '';
		},
		valido() {
			return this.intestazioni.length > 0 && this.campi.every(c => !c.required || this.mapping[c.key]);
		},
	},
	watch: {
		file: { immediate: true, handler() { this.carica(); } },
		mapping: {
			deep: true,
			handler() {
				const out = {};
				Object.keys(this.mapping).forEach(k => { if (this.mapping[k]) out[k] = this.mapping[k]; });
				this.$emit('update:modelValue', out);
				this.$emit('valido', this.valido);
			},
		},
	},
	methods: {
		carica() {
			this.intestazioni = [];
			this.righe = [];
			this.rigaEsempio = 0;
			this.mapping = {};
			this.aperto = true;
			if (!this.file) return;

			const formData = new FormData();
			formData.append('file1', this.file);
			formData.append('args', JSON.stringify({ modalita: 'anteprimaExcel' }));
			utils.uploadAjax('core/basic/upload', formData, (res) => {
				if (res.esito != 0) return;
				this.intestazioni = (res.data.INTESTAZIONI || []).map(String);
				this.righe = res.data.RIGHE || [];
				this.caricaPreset(true);
			});
		},

		caricaPreset(conAutoMatch) {
			utils.get('core/importmapping', { TIPO_IMPORT: this.tipoImport }, (res) => {
				this.preset = res.esito == 0 ? (res.data || []).map(p => ({
					...p,
					MAPPING:      JSON.parse(p.MAPPING || '{}'),
					INTESTAZIONI: JSON.parse(p.INTESTAZIONI || '[]'),
				})) : [];
				if (conAutoMatch) this.autoMatch();
			}, false, 1);
		},

		autoMatch() {
			const firma = (arr) => arr.map(normalizza).sort().join('|');
			const firmaFile = firma(this.intestazioni);
			const trovato = this.preset.find(p => firma(p.INTESTAZIONI) == firmaFile);
			if (trovato) {
				this.idPreset = trovato.ID_IMPORT_MAPPING;
				this.applicaPreset(trovato);
				this.messaggioMatch = `Riconosciuto il preset "${trovato.NOME}".`;
				this.aperto = false;
				return;
			}

			// ponytail: greedy per punteggio decrescente, O(campi x colonne) - basta per file con decine di colonne
			const coppie = [];
			this.campi.forEach(campo => {
				this.intestazioni.forEach(h => {
					const p = this.punteggio(h, campo);
					if (p >= SOGLIA_FUZZY) coppie.push({ key: campo.key, h, p });
				});
			});
			coppie.sort((a, b) => b.p - a.p);

			const mapping = {};
			const usate = new Set();
			coppie.forEach(c => {
				if (mapping[c.key] || usate.has(c.h)) return;
				mapping[c.key] = c.h;
				usate.add(c.h);
			});
			this.campi.forEach(c => { if (!mapping[c.key]) mapping[c.key] = ''; });
			this.idPreset = null;
			this.mapping = mapping;
			this.messaggioMatch = 'Abbinamenti proposti in automatico: controlla prima di proseguire.';

			// tabella nascosta solo se ogni colonna abbinata ha esattamente il nome del campo (no alias, no fuzzy)
			const tuttiEsatti = this.campi.every(c => !mapping[c.key] || [c.key, c.label].map(normalizza).includes(normalizza(mapping[c.key])));
			const obbligatoriCompleti = this.campi.every(c => !c.required || mapping[c.key]);
			this.aperto = !(tuttiEsatti && obbligatoriCompleti);
		},

		punteggio(intestazione, campo) {
			const h = normalizza(intestazione);
			if (!h) return 0;
			const nomi = [campo.key, campo.label, ...(campo.alias || [])].map(normalizza).filter(Boolean);
			if (nomi.includes(h)) return 1;
			return Math.max(...nomi.map(n => 1 - levenshtein(h, n) / Math.max(h.length, n.length)));
		},

		applicaPreset(p) {
			const mapping = {};
			this.campi.forEach(c => {
				const h = p.MAPPING[c.key];
				mapping[c.key] = h && this.intestazioni.includes(h) ? h : '';
			});
			this.mapping = mapping;
		},

		applicaPresetSelezionato() {
			const p = utils.array.select(this.preset, 'ID_IMPORT_MAPPING', this.idPreset, 1)[0];
			if (p) {
				this.applicaPreset(p);
				this.messaggioMatch = `Applicato il preset "${p.NOME}".`;
			}
			else {
				this.autoMatch();
			}
		},

		isUsata(h, key) {
			return Object.keys(this.mapping).some(k => k != key && this.mapping[k] == h);
		},

		esempio(campo) {
			const h = this.mapping[campo.key];
			const riga = this.righe[this.rigaEsempio];
			if (!h || !riga) return '';
			const v = riga[h];
			// seriale Excel (giorni dal 30/12/1899) -> data leggibile
			if (campo.tipo == 'data' && typeof v == 'number') {
				return new Date(Math.round((v - 25569) * 86400000)).toLocaleDateString('it-IT', { timeZone: 'UTC' });
			}
			return v == null ? '' : String(v);
		},

		salvaPreset() {
			utils.post('core/importmapping/insert', {
				TIPO_IMPORT:  this.tipoImport,
				NOME:         this.nomePreset,
				INTESTAZIONI: this.intestazioni,
				MAPPING:      this.modelValue,
			}, (res) => {
				if (res.esito == 0) {
					utils.alert.success('Preset salvato');
					this.messaggioMatch = `Preset "${this.nomePreset}" salvato: verrà riconosciuto automaticamente.`;
					this.nomePreset = '';
					this.caricaPreset(false);
				}
			});
		},

		eliminaPreset() {
			const p = utils.array.select(this.preset, 'ID_IMPORT_MAPPING', this.idPreset, 1)[0];
			if (!p) return;
			utils.alert.confirm(`Eliminare il preset "${p.NOME}"?`, () => {
				utils.delete('core/importmapping/delete', { ID_IMPORT_MAPPING: p.ID_IMPORT_MAPPING }, (res) => {
					if (res.esito == 0) {
						this.idPreset = null;
						this.caricaPreset(false);
					}
				});
			}, this.$root);
		},
	},
};
</script>
